import { Suspense } from "react";
import AppErrorBoundary from "@/components/AppErrorBoundary";
import { lazyWithRetry } from "@/lib/lazyWithRetry";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";

// Lazy-loaded pages — each route gets its own chunk
const Index = lazyWithRetry(() => import("./pages/Index"));
const NotFound = lazyWithRetry(() => import("./pages/NotFound"));
const Auth = lazyWithRetry(() => import("./pages/Auth"));
const PhotographerAuth = lazyWithRetry(() => import("./pages/PhotographerAuth"));
const PhotographerDashboard = lazyWithRetry(() => import("./pages/PhotographerDashboard"));
const PhotographerEdit = lazyWithRetry(() => import("./pages/PhotographerEdit"));
const PhotographerRegister = lazyWithRetry(() => import("./pages/PhotographerRegister"));
const PhotographerConfirmEmail = lazyWithRetry(() => import("./pages/PhotographerConfirmEmail"));

// Prefetch common photographer entry points after initial load
if (typeof window !== "undefined") {
  window.addEventListener("load", () => {
    setTimeout(() => {
      // Best-effort: a failed prefetch must not surface as an unhandled
      // rejection, the route will load again on navigation.
      void import("./pages/PhotographerAuth").catch(() => {});
      void import("./pages/PhotographerRegister").catch(() => {});
    }, 2000);
  }, { once: true });
}
const ResetPassword = lazyWithRetry(() => import("./pages/ResetPassword"));
const PhotographerPublicProfile = lazyWithRetry(() => import("./pages/PhotographerPublicProfile"));
const PhotographerSubscription = lazyWithRetry(() => import("./pages/PhotographerSubscription"));
const MentionsLegales = lazyWithRetry(() => import("./pages/MentionsLegales"));
const PolitiqueConfidentialite = lazyWithRetry(() => import("./pages/PolitiqueConfidentialite"));
const CGU = lazyWithRetry(() => import("./pages/CGU"));
const Contact = lazyWithRetry(() => import("./pages/Contact"));

// Lazy-load toast providers — not needed for first paint
const Toaster = lazyWithRetry(() => import("@/components/ui/toaster").then(m => ({ default: m.Toaster })));
const Sonner = lazyWithRetry(() => import("@/components/ui/sonner").then(m => ({ default: m.Toaster })));

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5, // 5 min — avoid refetching on every navigation
      gcTime: 1000 * 60 * 10,   // 10 min — keep cache longer
      refetchOnWindowFocus: false,
      retry: 1,
    },
  },
});

// Uses inline CSS from index.html for instant FCP before main CSS bundle loads
const PageFallback = () => (
  <div className="page-fallback">
    <div className="spinner" />
  </div>
);

const App = () => (
  <AppErrorBoundary>
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        <Suspense fallback={null}>
          <Toaster />
          <Sonner />
        </Suspense>
        <BrowserRouter>
          <Suspense fallback={<PageFallback />}>
            <Routes>
              <Route path="/" element={<Navigate to="/photographer/auth" replace />} />
              <Route path="/auth" element={<Navigate to="/photographer/auth" replace />} />
              <Route path="/admin/auth" element={<Auth />} />
              <Route path="/admin" element={<Index />} />
            
              <Route path="/photographer/auth" element={<PhotographerAuth />} />
              <Route path="/photographer/register" element={<PhotographerRegister />} />
              <Route path="/photographer/confirm-email" element={<PhotographerConfirmEmail />} />
              <Route path="/photographer/dashboard" element={<PhotographerDashboard />} />
              <Route path="/photographer/edit" element={<PhotographerEdit />} />
              <Route path="/photographer/reset-password" element={<ResetPassword />} />
              <Route path="/photographer/subscription" element={<PhotographerSubscription />} />
              <Route path="/photographe/:id" element={<PhotographerPublicProfile />} />
              <Route path="/photo/:id" element={<PhotographerPublicProfile />} />
              <Route path="/mentions-legales" element={<MentionsLegales />} />
              <Route path="/politique-de-confidentialite" element={<PolitiqueConfidentialite />} />
              <Route path="/cgu" element={<CGU />} />
              <Route path="/contact" element={<Contact />} />
              {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
              <Route path="*" element={<NotFound />} />
            </Routes>
          </Suspense>
        </BrowserRouter>
      </TooltipProvider>
    </QueryClientProvider>
  </AppErrorBoundary>
);

export default App;
